import { Button, Tour } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { Position } from "@turf/turf";
import { toast } from "react-toastify";
import { MapType } from "@/features/map/utils/agromap.type";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { SeasonMap } from "@/features/map/utils/seasonmap";
import { locateMe } from "@/features/map/utils/geolocator";
import { seosonSteps } from "@/tour_steps/growingseasson";
import { IGeoShapeDetails } from "@/features/map/domain/IGeoShapeDetails";
import { Season } from "@/features/growing-season/domain/growing-season.interface";
// @ts-ignore
import Cancel from "@assets/icons/cancel.png";
// @ts-ignore
import Confirm from "@assets/icons/confirm.png";
// @ts-ignore
import Location from "@assets/icons/location.png";

interface AgroMapSeasonProps {
  type: MapType;
  mapId: string;
  toolbarOptions: L.PM.ToolbarOptions;
  markerIcon: L.Icon<L.IconOptions> | L.DivIcon;
  parentFarmPolygon: Position[];
  farmSeasons?: Season[];
  currentSeasonPolygon?: Position[];
  currentSeasonProductId?: number;
  onConfirm: (data: IGeoShapeDetails) => void;
  onCancel: () => void;
}

const AgroMapSeason = ({
  type,
  mapId,
  toolbarOptions,
  markerIcon,
  parentFarmPolygon,
  farmSeasons,
  currentSeasonPolygon,
  currentSeasonProductId,
  onConfirm,
  onCancel,
}: AgroMapSeasonProps) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const seasonMap = new SeasonMap(
    popupRef,
    toolbarOptions,
    markerIcon,
    type,
    parentFarmPolygon,
    farmSeasons,
    currentSeasonPolygon
  );

  const [openTour, setOpenTour] = useState(false);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);

  const parentWithoutOtherSeasons: Position[] | Position[][] | null =
    useMemo(() => {
      if (farmSeasons && farmSeasons.length) {
        return seasonMap.polygonsDifference(
          parentFarmPolygon,
          farmSeasons?.map((season) => season.polygon) as Position[][]
        );
      } else {
        return parentFarmPolygon;
      }
    }, [parentFarmPolygon]);

  function confirm() {
    if (type === "edit") {
      const validation = seasonMap.validate(
        seasonMap.polygonDetails.polygon.length > 0
          ? seasonMap.polygonDetails.polygon
          : currentSeasonPolygon!
      );
      if (validation.isValid) {
        onConfirm(seasonMap.polygonDetails);
      } else {
        toast.error(validation.message);
        // Swal.fire({
        //   title: "خطا در رسم نقشه",
        //   text: validation.message,
        //   icon: "error",
        //   showCloseButton: true,
        //   confirmButtonText: "متوجه شدم",
        // });
      }
    } else {
      onConfirm(seasonMap.polygonDetails);
    }
  }

  function confirmFarmAsSeason() {
    seasonMap.setPolygonDetails(
      parentWithoutOtherSeasons as Position[],
      "[latitude | longitude]"
    );
    onConfirm(seasonMap.polygonDetails);
  }

  useEffect(() => {
    if (parentWithoutOtherSeasons && type === "add") {
      seasonMap.parentFarmPolygon = parentWithoutOtherSeasons as Position[];
    }

    if (document.getElementById(`map${mapId}`)) {
      seasonMap.init(mapId);

      //  نمایش زمین بدون فصل رشدی هایش
      seasonMap.mapPolygon(seasonMap.parentFarmPolygon, false, true, -1);

      // نمایش فصل رشدی های زمین
      farmSeasons?.forEach((season) => {
        seasonMap.mapPolygon(
          season.polygon,
          false,
          true,
          season.product.productId!
        );
      });

      currentSeasonPolygon &&
        currentSeasonPolygon?.length > 0 &&
        seasonMap.mapPolygon(
          currentSeasonPolygon,
          type !== "onlyShow",
          true,
          currentSeasonProductId
        );
      seasonMap.initEditListeners();
    }
  }, [parentWithoutOtherSeasons, currentSeasonPolygon]);

  return parentWithoutOtherSeasons ? (
    <div ref={ref1} className="relative h-full w-full rounded-lg font-body">
      {/* map */}
      <div id={`map${mapId}`} className="h-full w-full rounded-lg" />
      {/* confirmation controls */}

      {type !== "onlyShow" && (
        <>
          <div
            id="searchAndHelp"
            className="flex max-lg:flex-col absolute gap-4 max-lg:gap-1 left-0 max-lg:w-40 top-4 z-[999] "
          >
            <Button
              className="circle-neo flex items-center justify-center text-[#008a44] hover:!text-[#50aa44] font-bold !rounded-xl max-lg:w-16 max-lg:absolute left-0 top-20 !rounded-l-none"
              icon={<QuestionCircleOutlined className="text-base" />}
              onClick={() => setOpenTour(true)}
            >
              راهنما
            </Button>
          </div>
          <div
            id="confirmationControls"
            className="flex flex-wrap-reverse absolute gap-4 max-lg:gap-1 right-4 bottom-4 z-[999]"
          >
            <div
              onClick={onCancel}
              id="cancelButton"
              className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 w-20 max-lg:w-16 rounded-2xl px-3 cursor-pointer"
            >
              <img
                alt=""
                src={Cancel}
                className="h-8 w-8 max-lg:w-4 max-lg:h-4"
              />
              <p>لغو</p>
            </div>
            {/* confirm button, only shows when polygon created by user and it's length is not equal to zero*/}

            <div
              onClick={confirm}
              id="confirmButton"
              className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 w-20 max-lg:w-16 rounded-full px-3 cursor-pointer"
            >
              <img
                alt=""
                src={Confirm}
                className="h-8 w-8 max-lg:w-4 max-lg:h-4"
              />{" "}
              <p>تایید</p>{" "}
            </div>
            {type === "add" && (
              <div
                ref={ref2}
                onClick={confirmFarmAsSeason}
                id="confirmButton"
                className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 max-lg:w-36 whitespace-nowrap rounded-full px-3 cursor-pointer"
              >
                <img
                  alt=""
                  src={Confirm}
                  className="h-8 w-8 max-lg:w-4 max-lg:h-4"
                />{" "}
                <p>انتخاب کل محدوده</p>{" "}
              </div>
            )}
          </div>
        </>
      )}

      {/* popUp area */}
      <div
        ref={popupRef}
        id="popup"
        className="absolute hidden h-8 w-64 max-lg:w-fit max-lg:bottom-4 gap-4 left-5 bottom-3 z-[999] bg-[#E9F0F7] rounded-full justify-start items-center max-lg:pl-8 px-4"
      >
        <p></p>
      </div>
      {/* locator button */}
      {type !== "onlyShow" && (
        <div
          id="locator"
          className="circle-neo absolute flex justify-center items-center p-2 left-2 bottom-2.5 max-lg:bottom-3 max-lg:left-2 h-10 w-10 rounded-full z-[999] cursor-pointer"
          onClick={() => locateMe(seasonMap.map!)}
        >
          <img src={Location} alt="" />
        </div>
      )}
      {/* tour guide */}
      <Tour
        rootClassName="absolute !bg-green-500 "
        type="primary"
        open={openTour}
        steps={seosonSteps(ref1, ref2)}
        onClose={() => {
          setOpenTour(false);
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col justify-around items-center h-full w-full rounded-lg font-body p-4 gap-4">
      <p>
        برای فصل رشدی و تاریخ کاشت انتخابی شما قبلا کل زمین به عنوان محدوده
        انتخاب شده است، بنابراین امکان افزودن فصل رشدی برای تاریخ انتخابی شما
        وجود ندارد.
      </p>

      <div
        onClick={onCancel}
        id="cancelButton"
        className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 w-30 max-lg:w-24 rounded-2xl px-3 cursor-pointer"
      >
        <img alt="" src={Cancel} className="h-8 w-8 max-lg:w-4 max-lg:h-4" />
        <p>بازگشت</p>
      </div>
    </div>
  );
};

export default AgroMapSeason;
